<template>
    <Layout>

        <div class="fruit-catalog-wrapper" :class="{ 'embed-mode': isEmbed }">
            <!-- Hero Intro Section: Banner giới thiệu đầu trang -->
            <section v-if="!isEmbed" class="hero-intro-section">
                <div class="hero-intro-banner">
                    <!-- Biểu tượng hoa sen/lá cách điệu ở góc phải -->
                    <div class="banner-bg-icon">
                        <span class="isax isax-global-search"></span>
                    </div>
                    <div class="banner-content">
                        <div class="badge-tag">
                            <span class="isax isax-verify"></span> {{ $t('catalog.lotus_land') || 'Đất Sen Hồng' }}
                        </div>
                        <h1 class="banner-title">{{ $t('catalog.title') || 'Danh mục Trái cây đặc sản' }}</h1>
                        <p class="banner-desc">
                            {{ $t('catalog.description') }}
                        </p>
                    </div>
                    <div class="banner-logo-wrapper">
                        <img src="@/assets/images/banner/logo.png" alt="Logo Lễ hội" class="banner-logo" />
                    </div>
                </div>
            </section>

            <!-- Title for Embed/Iframe Mode -->
            <section v-else class="embed-title-section">
                <h2 class="embed-title">
                    <span class="isax isax-verify"></span>
                    {{ $t('catalog.title') || 'Danh mục Trái cây đặc sản' }}
                </h2>
            </section>

            <!-- Search & Filter Bar: Bộ tìm kiếm & Lọc bento -->
            <section class="filter-section" :class="{ 'embed-filter-section': isEmbed }">
                <div class="filter-bar-container" :class="{ 'embed-filter-container': isEmbed }">
                    <!-- Ô tìm kiếm tên sản phẩm -->
                    <div class="search-box-wrapper">
                        <span class="isax isax-search-normal-1 search-icon"></span>
                        <input v-model="itemFilter.content" type="text" class="search-input"
                            :placeholder="$t('catalog.search_placeholder') || 'Tìm kiếm tên trái cây đặc sản...'"
                            @keyup.enter="handleSearch" />
                    </div>

                    <button type="button" class="btn-filter-submit" @click="handleSearch">
                        <span class="isax isax-filter"></span>
                    </button>
                </div>
            </section>

            <!-- Fruit Grid: Danh sách nông sản hiển thị dạng Bento-Grid -->
            <section class="catalog-grid-section">
                <div v-if="loading" class="loading-state">
                    <div class="spinner"></div>
                    <p>{{ $t('catalog.loading') || 'Đang tải danh mục nông sản...' }}</p>
                </div>

                <div v-else-if="fruitsList.length === 0" class="empty-state">
                    <span class="isax isax-box-remove empty-icon"></span>
                    <p>{{ $t('catalog.empty_message') || 'Không tìm thấy sản phẩm trái cây nào phù hợp với bộ lọc.' }}
                    </p>
                    <button v-if="!isEmbed" type="button" class="btn-reset-filters" @click="handleClearFilters">{{
                        $t('catalog.reset_filters') || 'Làm mới bộ lọc' }}</button>
                </div>

                <div v-else class="fruit-grid">
                    <!-- Fruit Card Item -->
                    <div v-for="item in fruitsList" :key="item._id" class="fruit-card">
                        <!-- Khu vực ảnh & nhãn chứng chỉ OCOP/VietGAP -->
                        <div class="card-media-wrapper">
                            <img :src="getImageUrl(item.avatar)" :alt="getLangValueFromItem(item, 'name')"
                                class="fruit-image" />
                            <!-- Badges động từ dữ liệu model -->
                            <div class="card-badges-container">
                                <span v-if="item.isChungNhan" class="badge bg-ocop">{{ $t('catalog.certified') ||
                                    'Đã Chứng Nhận' }}</span>
                                <span v-if="item.code === '03' || item.unsignedName === 'SAURIENG'"
                                    class="badge bg-region">{{ $t('catalog.growing_zone') || 'Mã vùng trồng' }}</span>
                            </div>
                            <!-- QR Code overlay in top-right corner of the image -->
                            <div v-if="item.qrCode2 && item.qrCode2.fileId" class="card-qr-overlay">
                                <img :src="apiView + item.qrCode2.fileId" alt="QR Code" class="card-qr-image" />
                            </div>
                        </div>

                        <!-- Khu vực thông tin -->
                        <div class="card-info-content">
                            <h3 class="fruit-card-title">{{ getLangValueFromItem(item, 'name') }}</h3>
                            <p class="card-scientific-name">{{ item.tenKhoaHoc || tenKhoaHocFallback(item) }}</p>

                            <div class="card-spec-row">
                                <span class="isax isax-verify spec-icon"></span>
                                <span class="spec-text">
                                    {{ getSpecText(item) }}
                                </span>
                            </div>

                            <!-- Nút hành động xem chi tiết (chuyển đổi trang hoặc emit sự kiện lên trang cha) -->
                            <button type="button" class="btn-view-detail" @click="handleViewDetail(item)">
                                {{ $t('catalog.view_detail') || 'Xem chi tiết' }}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <!-- View All Button for Embed/Iframe mode -->
            <div v-if="isEmbed" class="view-all-container">
                <a href="https://qrcode.lehoitraicaydongthap.vn/trai-cay/danh-sach" target="_blank"
                    class="btn-view-all">
                    {{ $t('catalog.view_all') || 'Xem tất cả' }}
                    <span class="isax isax-arrow-right"></span>
                </a>
            </div>

            <!-- Pagination: Bộ phận chuyển trang thiết kế chuẩn -->
            <nav class="pagination-wrapper" v-if="totalRows > (isEmbed ? 4 : perPage) && !isEmbed">
                <button type="button" class="page-nav-btn" :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)">
                    <span class="isax isax-arrow-left"></span>
                </button>

                <button v-for="page in totalPages" :key="page" type="button"
                    :class="['page-number-btn', currentPage === page ? 'page-active' : '']" @click="changePage(page)">
                    {{ page }}
                </button>

                <button type="button" class="page-nav-btn" :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)">
                    <span class="isax isax-arrow-right"></span>
                </button>
            </nav>
        </div>
    </Layout>
</template>

<script>
import Layout from "@/layouts/layout.vue";

export default {
    name: "FruitCatalog",
    components: {
        Layout
    },
    data() {
        return {
            apiView: process.env.VUE_APP_API_URL + 'filesminio/view/',
            currentPage: 1,
            perPage: 12,
            totalRows: 0,
            loading: false,
            itemFilter: {
                content: "",
                category: null,
                certification: null
            },
            fruitsList: [],
            // Mock Data dự phòng chất lượng cao (theo chuẩn dữ liệu thực tế)
            mockFruits: [
                {
                    _id: "647f1234567890abcdef1234",
                    name: "Xoài Cát Chu",
                    nameEn: "Cat Chu Mango",
                    unsignedName: "XOAICATCHU",
                    tenKhoaHoc: "Mangifera indica L.",
                    dacTinhSinhHoc: "Vỏ mỏng, khi chín có màu vàng tươi, thịt quả vàng đậm, dẻo ngọt.",
                    dacTinhSinhHocEn: "Thin skin, bright yellow when ripe, deep yellow flesh, sweet and chewy.",
                    dienTichSanLuong: "836 ha tập trung",
                    dienTichSanLuongEn: "836 ha concentrated",
                    vungTrongTapTrung: "Xã Cao Lãnh, Đồng Tháp",
                    vungTrongTapTrungEn: "Cao Lanh Commune, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuBxqqr3aTpnsgq5JCajrT95tysIj83wEtgU378ut01zIUPGqcXq4yLzwbOF4L1KcrdcHWIjcrPXz16Zy-w7lVglMW9t32ZMNMP_sKFbjOXM0R_nvm8J63YpbCdpbZcE9V94rppODKKFOs0EYIqrW1wh2Z9LIFEiwIORn506KrcJsvv278su74KBXIXcdyo2ytoqXm8wDL4of5ZD-qsTUyIXiNEPXwdgoqCYwqBCAMng_3W1RoqBdwLoDOtNL1GrzRNZT_8UkBZzdncu" },
                    ghiChu: "VietGAP, OCOP 4★",
                    ghiChuEn: "VietGAP, OCOP 4★"
                },
                {
                    _id: "647f1234567890abcdef1235",
                    name: "Nhãn Châu Thành",
                    nameEn: "Chau Thanh Longan",
                    unsignedName: "NHANCHAUTHANH",
                    tenKhoaHoc: "Dimocarpus longan",
                    dacTinhSinhHoc: "Cơm dày, hạt lép, vị ngọt đậm thơm mát miệt sông Tiền.",
                    dacTinhSinhHocEn: "Thick pulp, small seed, sweet and aromatic flavor from the Tien River region.",
                    dienTichSanLuong: "15.000 tấn/năm",
                    dienTichSanLuongEn: "15,000 tons/year",
                    vungTrongTapTrung: "Xã Châu Thành, Đồng Tháp",
                    vungTrongTapTrungEn: "Chau Thanh Commune, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuD6w18uAWoCYxB6neuPdl1_p02lCt31B2_I0KiFNTcMW9mCl7xDG5_45nqkoaDzEBkX-xXig4WJSZgh3wQm2CWcPmMGUFKQmOx0DzqjFw1de4ilR54vGcKOF6eTaPOwrjqt_Z5o56HMHHugIZcQGeKJLjaXS7DpifMLH805ZyLHWRZgVfnwfSpDavuRUvoi6tchTSHE5xrca_hQ6gzFCG-6tBrb0_h9a9ajXzJCIJMHbD90xZ_K_Jh1eZg1nfG4JnqkY-38NLa4jJzI" },
                    ghiChu: "GlobalGAP",
                    ghiChuEn: "GlobalGAP"
                },
                {
                    _id: "647f1234567890abcdef1236",
                    name: "Bưởi Phong Hòa",
                    nameEn: "Phong Hoa Pomelo",
                    unsignedName: "BUOIPHONGHOA",
                    tenKhoaHoc: "Citrus maxima",
                    dacTinhSinhHoc: "Tép bưởi hồng nhạt, mọng nước, vị chua ngọt hài hòa dễ chịu.",
                    dacTinhSinhHocEn: "Light pink pulp, juicy, pleasant balanced sweet and sour taste.",
                    dienTichSanLuong: "Lai Vung - Đồng Tháp",
                    dienTichSanLuongEn: "Lai Vung - Dong Thap",
                    vungTrongTapTrung: "Xã Phong Hòa, Lai Vung, Đồng Tháp",
                    vungTrongTapTrungEn: "Phong Hoa Commune, Lai Vung, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuDODh6xsWZeGiZXKD6BuL8IJtJImzz9brl-luOgjHIg8jez3A9bB-mXA9k08NFp7TeTrFLkJJXMod_t0RLqHQkRTFZeW9g2R1Um7Xcofz0NfRzP3x84itEnRqHpybYwaOW_tX35zHOecF2ER_G8I9vW26UIRjTOB32__AOHZeAX4DOBqJ8u7rQcgqeUs5yZiXqlYIc2wFZrqEswEDX2FM43lqyWH7EAYpX3o8iuOyBUARe-uS2QMrvM1hS8BC9dpRWXMrqaIR9BwtUQ" },
                    ghiChu: "OCOP 5★",
                    ghiChuEn: "OCOP 5★"
                },
                {
                    _id: "647f1234567890abcdef1237",
                    name: "Sầu riêng Ri6",
                    nameEn: "Ri6 Durian",
                    unsignedName: "SAURIENG",
                    tenKhoaHoc: "Durio zibethinus",
                    dacTinhSinhHoc: "Cơm vàng, hạt lép, dẻo béo thơm lừng nức tiếng.",
                    dacTinhSinhHocEn: "Yellow flesh, shriveled seed, sweet and rich aroma.",
                    dienTichSanLuong: "Mã số vùng trồng xuất khẩu",
                    dienTichSanLuongEn: "Export planting zone code",
                    vungTrongTapTrung: "Xã Cao Lãnh, Đồng Tháp",
                    vungTrongTapTrungEn: "Cao Lanh Commune, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuCYllDC-OQxeed3ewOfAGbqovka9h0aXnxosFy7ugAxBxkZnlNOB4EhpNqG-eD0BVtnugthjtJo0H-yBFRUSEXx0MAkLxENmaZGbIq1D9g0aFmXzqNbgBtYoawRXxTfmwVuUknnhSEPh9gcjbC1l9zYaL9NoPAjmdgAdeMYHI_GtSUisVlSl2f2OKH0LfTX4rOrl2YUcFzQRFxuV33JlO8lMuZB4jWw0QTUG4QNCRP3qF6rny34qOVOPKFHGhQ3HGXozEEcKAGfGZAY" },
                    code: "03"
                },
                {
                    _id: "647f1234567890abcdef1238",
                    name: "Quýt Hồng Lai Vung",
                    nameEn: "Lai Vung Pink Mandarin",
                    unsignedName: "QUYTHONG",
                    tenKhoaHoc: "Citrus reticulata",
                    dacTinhSinhHoc: "Màu hồng cam bóng bẩy đặc trưng, tép ngọt thanh mọng nước.",
                    dacTinhSinhHocEn: "Characteristic glossy pinkish-orange color, juicy sweet and fresh segments.",
                    dienTichSanLuong: "300+ ha canh tác",
                    dienTichSanLuongEn: "300+ ha cultivation",
                    vungTrongTapTrung: "Lai Vung, Đồng Tháp",
                    vungTrongTapTrungEn: "Lai Vung, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuC3-xGLLwfy0Czqlyuf8lV_kv6VzJl7o0kmWIus3mYf_y8yIClXJjg4oGQXdk93I6w3MYb1726VV9GAVLB_VUmzGMF-8UsMa_av2d_Pr4vcmuMNrI8RDGMFako7LPYZy_D19Wd6tEuEZo6OQ_ekYDhkNGq203S-vhphMifXMYR6JegZlohEdSgzCKnslyaidCZ5y9B0hN_J__jG3Ujl_wH8qAKEAEB0LiF-XXIt8YlSMsbCeDqahtLnQc3KxPJzLmhfoEJxUFyNZeD3" },
                    ghiChu: "Chỉ dẫn địa lý",
                    ghiChuEn: "Geographical Indication"
                },
                {
                    _id: "647f1234567890abcdef1239",
                    name: "Mận Hòa An",
                    nameEn: "Hoa An Wax Apple",
                    unsignedName: "MANHOAAN",
                    tenKhoaHoc: "Syzygium samarangense",
                    dacTinhSinhHoc: "Đỏ đậm ngọt mát trái tròn giòn dẻo.",
                    dacTinhSinhHocEn: "Dark red, sweet and cool, round, crispy and chewy.",
                    dienTichSanLuong: "Đặc sản địa phương",
                    dienTichSanLuongEn: "Local specialty",
                    vungTrongTapTrung: "Thành phố Cao Lãnh, Đồng Tháp",
                    vungTrongTapTrungEn: "Cao Lanh City, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuCxY8L-S3pfmXNiV8lzwUvtOmr9D4UCdZEC0fiGeVgGNODgYcTraYWpgYIQ5mLIvBVkr3h_qbh-SdmA4tSgv74c-vxz0xlqwMbaYvoJFBQMKoo1o48_oBYmWZxUlD0EudQ3Z-8_17FcacEs7-cBW6BtxgkQzMoqdov7AXcMQIEfzeIW87CkwJ_wyRSjU3AVZ4_Xoc9r0_miZVaT1aO9JrZ220Et6DweaESiB2O7X36akPhZn_m8yerrB4aZ-JbG9FebcympAuu9olmW" },
                    ghiChu: "Hữu cơ",
                    ghiChuEn: "Organic"
                },
                {
                    _id: "647f1234567890abcdef1240",
                    name: "Chanh Cao Lãnh",
                    nameEn: "Cao Lanh Seedless Lime",
                    unsignedName: "CHANHCAOLANH",
                    tenKhoaHoc: "Citrus aurantifolia",
                    dacTinhSinhHoc: "Chanh không hạt vỏ xanh mọng dồi dào nước.",
                    dacTinhSinhHocEn: "Seedless lime, glossy green skin, rich in juice.",
                    dienTichSanLuong: "Chứng nhận GlobalGAP",
                    dienTichSanLuongEn: "GlobalGAP Certified",
                    vungTrongTapTrung: "Xã Cao Lãnh, Đồng Tháp",
                    vungTrongTapTrungEn: "Cao Lanh Commune, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuCNSntkUtJ5hLp47hIGa022nYkmHnqpmfYZHf7J2GWqa5q4l4fEscuTu5lBPWbXISzCukD_pVfPf7ptuKfNY_4YB57Dct6KiTRhBRhhybVLRK5eg3DR5SfHkzQxQoXfIuGG4bRrLQBeUIzlIle736lTbgg_pxABlAhrU99KhIHie-2zoPI3KYe7Nuusr8o1_MQ4X4siW0Y-HRNt4gKfQIcSi0WfhAaz0uk6aUeqP_IXa6tTWc56WpwPzvzirXU4luRJiDIEKfSMturZ" },
                    ghiChu: "Xuất khẩu",
                    ghiChuEn: "Export quality"
                },
                {
                    _id: "647f1234567890abcdef1241",
                    name: "Thanh long Tháp Mười",
                    nameEn: "Thap Muoi Dragon Fruit",
                    unsignedName: "THANHLONGTHAPMUOI",
                    tenKhoaHoc: "Hylocereus undatus",
                    dacTinhSinhHoc: "Thanh long ruột đỏ ngọt đậm dồi dào vitamin.",
                    dacTinhSinhHocEn: "Red flesh dragon fruit, deep sweet, rich in vitamins.",
                    dienTichSanLuong: "Đặc sản Tháp Mười",
                    dienTichSanLuongEn: "Thap Muoi Specialty",
                    vungTrongTapTrung: "Xã Tháp Mười, Đồng Tháp",
                    vungTrongTapTrungEn: "Thap Muoi Commune, Dong Thap",
                    avatar: { path: "https://lh3.googleusercontent.com/aida-public/AB6AXuA9pqa4Xv69ggaUGSwj78fI5s5-xpW-47WPxQiOy7P83yH1h5SvF5rfrkL0GAPi25ZLVrAptuzErj8gI5Vyv4uagH9IWwgPEp9gmv1dSblxhoUzPKn4Er3rgxdy_vR9Tn2YTxjDbavcmhPG-f8AnMzZ6Vz2rExrvJ0dILJUU4Najn9KXzmuPTkZnjIBcrXDesw-MOJl7j9Ai_BtgxCX5a6Jue2gkD9nGS1aKXex7o5Zq6jc2RYblkR85wApikC2bd9ANPX_kRgaPIR7" },
                    ghiChu: "VietGAP",
                    ghiChuEn: "VietGAP"
                }
            ]
        };
    },
    computed: {
        totalPages() {
            const limit = this.isEmbed ? 4 : this.perPage;
            return Math.ceil(this.totalRows / limit) || 1;
        },
        isEmbed() {
            return this.$route.query.embed === 'true' || this.$route.query.iframe === 'true';
        }
    },
    created() {
        this.fetchFruits();
    },
    mounted() {
        if (this.isEmbed) {
            document.body.style.backgroundColor = '#ffffff';
        }
    },
    beforeDestroy() {
        if (this.isEmbed) {
            document.body.style.backgroundColor = '';
        }
    },
    methods: {
        getLangValue(fieldName) {
            const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
            return currentLocale === "en" ? `${fieldName}En` : fieldName;
        },
        getLangValueFromItem(item, fieldName) {
            if (!item) return "";
            const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
            if (currentLocale === "en") {
                const enField = `${fieldName}En`;
                return item[enField] || item[fieldName] || "";
            }
            return item[fieldName] || "";
        },
        // 🌐 FETCH DATA DỮ LIỆU ĐỘNG TỪ API (HỖ TRỢ TRẢ VỀ DỰ PHÒNG MOCKDATA)
        async fetchFruits() {
            this.loading = true;
            const limit = this.isEmbed ? 4 : this.perPage;
            const params = {
                start: this.currentPage,
                limit: limit,
                sortBy: "createdAt",
                sortDesc: true,
                content: this.itemFilter.content || null,

            };

            try {
                // Kiểm tra xem Store của dự án có tồn tại không trước khi dispatch để tránh crash
                if (this.$store && this.$store._actions["profession/traiCay/traiCayStore/getPagingParams"]) {
                    await this.$store.dispatch("profession/traiCay/traiCayStore/getPagingParams", params).then(resp => {
                        if (resp && resp.data) {
                            let dataList = resp.data.data || [];
                            if (this.isEmbed) {
                                this.fruitsList = dataList.slice(0, 4);
                                this.totalRows = 4;
                            } else {
                                this.fruitsList = dataList;
                                this.totalRows = resp.data.totalRows || this.fruitsList.length;
                            }
                        } else {
                            this.loadMockFallback();
                        }
                    });
                } else {
                    this.loadMockFallback();
                }
            } catch (error) {
                console.warn("API Error, fallback to mock data:", error);
                this.loadMockFallback();
            } finally {
                this.loading = false;
            }
        },
        // Trả về dữ liệu cục bộ nếu không có kết nối API
        loadMockFallback() {
            let filtered = [...this.mockFruits];

            // Tìm kiếm theo ký tự nhập
            if (this.itemFilter.content) {
                const query = this.itemFilter.content.toLowerCase();
                filtered = filtered.filter(f => {
                    const nameVal = this.getLangValueFromItem(f, 'name');
                    return nameVal.toLowerCase().includes(query);
                });
            }

            // Lọc theo chứng nhận (OCOP / VietGAP / GlobalGAP)
            if (this.itemFilter.certification) {
                const cert = this.itemFilter.certification.toLowerCase();
                filtered = filtered.filter(f => {
                    const ghiChuVal = this.getLangValueFromItem(f, 'ghiChu');
                    return ghiChuVal && ghiChuVal.toLowerCase().includes(cert);
                });
            }

            const limit = this.isEmbed ? 4 : this.perPage;
            this.totalRows = this.isEmbed ? 4 : filtered.length;

            // Thực hiện cắt mảng phục vụ phân trang offline
            const startIdx = (this.currentPage - 1) * limit;
            const endIdx = startIdx + limit;
            this.fruitsList = filtered.slice(startIdx, endIdx);
        },
        handleFilterChange() {
            this.currentPage = 1;
            this.fetchFruits();
        },
        handleSearch() {
            this.currentPage = 1;
            this.fetchFruits();
        },
        handleClearFilters() {
            this.itemFilter = {
                content: "",
                category: null,
                certification: null
            };
            this.currentPage = 1;
            this.fetchFruits();
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.fetchFruits();
            }
        },
        handleViewDetail(item) {
            const query = {};
            if (this.$route.query.embed) query.embed = this.$route.query.embed;
            if (this.$route.query.iframe) query.iframe = this.$route.query.iframe;
            this.$router.push({
                path: `/trai-cay/chi-tiet/${item.code}`,
                query
            });
        },
        // Kiểm tra nhanh chứng nhận có trong chuỗi ghi chú
        hasVietGAP(item) {
            const ghiChuVal = this.getLangValueFromItem(item, 'ghiChu');
            return ghiChuVal && ghiChuVal.toLowerCase().includes("vietgap");
        },
        hasOCOP(item) {
            const ghiChuVal = this.getLangValueFromItem(item, 'ghiChu');
            return ghiChuVal && ghiChuVal.toLowerCase().includes("ocop");
        },
        tenKhoaHocFallback(item) {
            const nameVal = this.getLangValueFromItem(item, 'name');
            if (item.unsignedName === "SAURIENG" || nameVal.toLowerCase().includes("sầu riêng") || nameVal.toLowerCase().includes("durian")) {
                return "Durio zibethinus";
            }
            return "Mangifera indica L.";
        },
        getSpecText(item) {
            const areaYield = this.getLangValueFromItem(item, 'dienTichSanLuong');
            const vungTrong = this.getLangValueFromItem(item, 'vungTrongTapTrung');
            const zoneText = this.$t('catalog.growing_zone_text') || 'Vùng trồng';
            return areaYield || `${zoneText}: ${vungTrong || 'Đồng Tháp'}`;
        },
        getImageUrl(file) {
            if (!file) return "";
            if (file.fileId) {
                return `${process.env.VUE_APP_API_URL}filesminio/view/${file.fileId}`;
            }
            return file.path || "";
        },
    }
};
</script>

<style scoped>
/* ======================== CHỦ ĐỀ MÀU SẮC & PHÔNG CHỮ CHUNG ======================== */
.fruit-catalog-wrapper {
    font-family: var(--font-body-standard), sans-serif;
    color: var(--on-background, #181d17);
    background-color: var(--background, #f7fbf0);
    line-height: 1.6;
    padding-top: 12px;
    padding-bottom: 32px;
    /* Đảm bảo khoảng cách an toàn với footer */
}

/* Embed/Iframe Mode Wrapper Padding & Background */
.fruit-catalog-wrapper.embed-mode {
    background-color: #ffffff !important;
    padding-top: 8px;
    padding-bottom: 16px;
    padding-left: 8px;
    padding-right: 8px;
}

/* ======================== EMBED TITLE SECTION ======================== */
.embed-title-section {
    max-width: var(--container-max, 1280px);
    margin: 0 auto 12px auto;
    padding: 0 16px;
    text-align: left;
}

.embed-mode .embed-title-section {
    text-align: center;
}

@media (min-width: 768px) {
    .embed-title-section {
        padding: 0 40px;
        margin-bottom: 16px;
    }
}

.embed-title {
    font-family: var(--font-heading-organic), sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--primary, #0d631b);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.embed-mode .embed-title {
    justify-content: center;
}

@media (min-width: 768px) {
    .embed-title {
        font-size: 1.7rem;
    }
}

/* ======================== HERO INTRO SECTION ======================== */
.hero-intro-section {
    max-width: var(--container-max, 1280px);
    margin: 0 auto 16px auto;
    /* Bỏ margin-top vì đã có padding-top ở wrapper */
    padding: 0 16px;
}

@media (min-width: 768px) {
    .hero-intro-section {
        margin: 0 auto 24px auto;
        padding: 0 40px;
    }
}

.hero-intro-banner {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    /* background-color: var(--primary-container, #2e7d32); */
    background-color: var(--color-public-primary, #92c3a8);
    padding: 32px 24px;
    /* Tăng nhẹ padding trên mobile */
    color: #ffffff;
    box-shadow: 0 4px 10px rgba(46, 125, 50, 0.15);
    display: flex;
    flex-direction: column;
    gap: 24px;
}

@media (min-width: 768px) {
    .hero-intro-banner {
        padding: 56px 48px;
        /* Tăng nhẹ padding trên desktop */
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 40px;
    }
}

.banner-logo-wrapper {
    display: none;
}

.banner-logo {
    max-height: 135px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 16px 14px rgba(0, 0, 0, 0.15));
    position: relative;
    z-index: 2;
}

@media (min-width: 576px) {
    .banner-logo-wrapper {
        display: block;
        align-self: center;
    }
}

@media (min-width: 992px) {
    .banner-logo {
        max-height: 210px;
    }
}

.banner-bg-icon {
    position: absolute;
    right: -24px;
    top: -24px;
    opacity: 0.1;
    pointer-events: none;
    font-size: 14.29rem;
    color: #ffffff;
}

.banner-content {
    max-width: 640px;
    text-align: left;
}

.badge-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    background-color: var(--primary-fixed, #a3f69c);
    color: var(--on-primary-fixed, #002204);
    border-radius: 9999px;
    font-size: 0.79rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 24px;
}

.banner-title {
    font-family: var(--font-heading-organic), sans-serif;
    font-size: 2.29rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 16px;
}

@media (min-width: 768px) {
    .banner-title {
        font-size: 3.43rem;
    }
}

.banner-desc {
    font-size: 1.14rem;
    color: var(--primary-fixed-dim, #88d982);
    line-height: 1.6;
    margin: 0;
}

/* ======================== SEARCH & FILTER BAR ======================== */
.filter-section {
    max-width: var(--container-max, 1280px);
    margin: 0 auto 16px auto;
    padding: 0 16px;
}

@media (min-width: 768px) {
    .filter-section {
        margin: 0 auto 24px auto;
        padding: 0 40px;
    }
}

.embed-filter-section {
    margin: 0 auto 12px auto;
    padding: 0 16px;
}

.filter-bar-container {
    background-color: var(--surface-container, #ebefe5);
    border: 1px solid var(--outline-variant, #bfcaba);
    padding: 16px;
    border-radius: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
}

.embed-filter-container {
    background-color: rgba(255, 255, 255, 0.75) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 0.5px solid rgba(255, 255, 255, 0.5) !important;
    border-radius: 12px;
    padding: 10px 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

@media (min-width: 768px) {
    .filter-bar-container {
        gap: 16px;
    }
}

/* Thanh tìm kiếm */
.search-box-wrapper {
    position: relative;
    flex: 1;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--outline, #707a6c);
    font-size: 1.29rem;
    pointer-events: none;
    line-height: 1;
}

.search-input {
    width: 100%;
    padding: 12px 16px 12px 48px;
    background-color: #ffffff;
    border: 1px solid var(--outline-variant, #bfcaba);
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    font-family: var(--font-body-standard), sans-serif;
    color: var(--on-surface-variant, #40493d);
    transition: border-color 0.2s;
    margin: 0;
}

.search-input:focus {
    border-color: var(--primary, #0d631b);
}

/* Các bộ lọc chọn lựa */
.select-filters-group {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
}

@media (min-width: 768px) {
    .select-filters-group {
        width: auto;
    }
}

.select-wrapper {
    position: relative;
    flex: 1;
}

@media (min-width: 768px) {
    .select-wrapper {
        width: 192px;
        flex: none;
    }
}

.filter-select {
    width: 100%;
    padding: 12px 40px 12px 16px;
    background-color: #ffffff;
    border: 1px solid var(--outline-variant, #bfcaba);
    border-radius: 8px;
    font-size: 1rem;
    outline: none;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23707a6c' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 16px;
    color: var(--on-surface-variant, #40493d);
}

.filter-select:focus {
    border-color: var(--primary, #0d631b);
}

.btn-filter-submit {
    padding: 12px;
    background-color: var(--secondary, #8f4e00);
    color: var(--on-secondary, #ffffff);
    border: none;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: opacity 0.2s;
    width: 48px;
    height: 48px;
    flex-shrink: 0;
}

.btn-filter-submit:hover {
    opacity: 0.9;
}

/* ======================== FRUIT GRID (DANH SÁCH THẺ CHUẨN) ======================== */
.catalog-grid-section {
    max-width: var(--container-max, 1280px);
    margin: 0 auto;
    padding: 0 16px;
}

@media (min-width: 768px) {
    .catalog-grid-section {
        padding: 0 40px;
    }
}

.fruit-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 576px) {
    .fruit-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 992px) {
    .fruit-grid {
        grid-template-columns: repeat(4, 1fr);
    }
}

/* Thẻ Nông sản */
.fruit-card {
    background-color: var(--surface, #f7fbf0);
    border: 1px solid var(--outline-variant, #bfcaba);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
}

.fruit-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
    border-color: var(--primary, #0d631b);
}

.card-media-wrapper {
    position: relative;
    height: 224px;
    overflow: hidden;
}

.fruit-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.fruit-card:hover .fruit-image {
    transform: scale(1.05);
}

/* Badges nổi trên ảnh */
.card-badges-container {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    gap: 8px;
}

.badge {
    font-family: var(--font-body-standard), sans-serif;
    font-size: 0.71rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 4px 8px;
    border-radius: 4px;
    color: #ffffff;
}

.bg-vietgap {
    background-color: var(--status-success, #4caf50);
}

.bg-ocop {
    background-color: var(--secondary-container, #ff8f06);
}

.bg-region {
    background-color: var(--primary-container, #2e7d32);
}

/* QR code overlay nổi trên ảnh bên phải */
.card-qr-overlay {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.95);
    padding: 1px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0.5px solid rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card-qr-overlay:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    background-color: #ffffff;
}

.card-qr-image {
    width: 65px;
    height: 65px;
    object-fit: contain;
    display: block;
}

/* Thông tin văn bản của Thẻ */
.card-info-content {
    padding: 20px;
    /* text-align: justify; */
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.fruit-card-title {
    font-family: var(--font-heading-organic), sans-serif;
    font-size: 1.43rem;
    color: var(--primary, #0d631b);
    font-weight: 700;
    text-align: left;
    margin: 0 0 4px 0;
}

.card-scientific-name {
    font-size: 0.95rem;
    font-style: italic;
    text-align: left;
    color: var(--outline, #707a6c);
    margin: 0 0 16px 0;
}

.card-spec-row {
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: justify;
    margin-bottom: 20px;
}

.spec-icon {
    font-size: 1.14rem;
    color: var(--status-success, #4caf50);
}

.spec-text {
    font-size: 0.95rem;
    color: var(--on-surface-variant, #40493d);
}

.btn-view-detail {
    width: 100%;
    padding: 10px;
    border: 1px solid var(--primary, #0d631b);
    background-color: transparent;
    color: var(--primary, #0d631b);
    font-family: var(--font-body-standard), sans-serif;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    margin-top: auto;
    transition: all 0.3s ease;
}

.fruit-card:hover .btn-view-detail {
    background-color: var(--primary, #0d631b);
    color: #ffffff;
}

/* Trạng thái trống (Empty State) & Tải (Loading) */
.loading-state,
.empty-state {
    text-align: center;
    padding: 48px 24px;
    color: var(--on-surface-variant, #40493d);
}

.spinner {
    width: 32px;
    height: 32px;
    border: 4px solid var(--outline-variant, #bfcaba);
    border-top-color: var(--primary, #0d631b);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px auto;
}

.empty-icon {
    font-size: 3.43rem;
    color: var(--outline, #707a6c);
    margin-bottom: 16px;
    display: block;
}

.btn-reset-filters {
    margin-top: 16px;
    padding: 8px 16px;
    border: none;
    background-color: var(--primary, #0d631b);
    color: #ffffff;
    border-radius: 8px;
    cursor: pointer;
}

/* ======================== PAGINATION (PHÂN TRANG) ======================== */
.pagination-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 24px;
    padding: 0 16px 16px 16px;
    /* Giảm padding bottom ở đây */
}

@media (min-width: 768px) {
    .pagination-wrapper {
        margin-top: 32px;
        padding: 0 40px 24px 40px;
    }
}

.page-nav-btn,
.page-number-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid var(--outline-variant, #bfcaba);
    background-color: #ffffff;
    color: var(--on-surface-variant, #40493d);
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    transition: all 0.2s;
    outline: none;
}

.page-nav-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.page-nav-btn:not(:disabled):hover,
.page-number-btn:hover {
    background-color: var(--surface-container, #ebefe5);
}

.page-number-btn.page-active {
    background-color: var(--primary, #0d631b);
    border-color: var(--primary, #0d631b);
    color: #ffffff;
    font-weight: 700;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* ======================== EMED / IFRAME VIEW ALL BUTTON ======================== */
.view-all-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    padding: 0 16px;
}

.btn-view-all {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 32px;
    background-color: var(--primary, #0d631b);
    color: #ffffff;
    font-family: var(--font-body-standard), sans-serif;
    font-size: 1rem;
    font-weight: 600;
    text-decoration: none;
    border-radius: 9999px;
    box-shadow: 0 4px 10px rgba(13, 99, 27, 0.2);
    transition: all 0.3s ease;
}

.btn-view-all:hover {
    background-color: var(--primary-container, #2e7d32);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(13, 99, 27, 0.3);
}

.btn-view-all .isax {
    font-size: 1.2rem;
    transition: transform 0.2s ease;
}

.btn-view-all:hover .isax {
    transform: translateX(4px);
}
</style>